import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, nextTick, onBeforeMount, ref } from 'vue';
import { getProductManualVariants, linkManualVariants } from '@/api/admin/templateProducts';
import {
    getPodProductsCatalog,
    getPodProductVariants as _getPodProductVariants,
    getPodProductFilters,
    getPodProductDetails as _getPodProductDetails,
    saveVariantsPrice,
    getVariantsPrice
} from '@/api/storesApp/podProviders';
import { MatchVariantAndPlaceholderPayload, TemplateProductVariant, Variant } from '@/api/typings/templateProducts';
import { GenericCatalog, GenericPodProduct, GenericPodVariant, ProductFilters } from '@/api/typings/podProviders';
import ProductSelector from '../ProductSelector/ProductSelector.vue';
import VariantMatchAssigner from './steps/VariantMatchAssigner.vue';
import PlaceholderSelector from './steps/PlaceholderSelector.vue';
import VariantPicker from './steps/VariantPicker.vue';
import VairantFilters from './steps/VariantFilters.vue';
import { Provider } from '@/utils/PodProvider.enum';

enum AssignerSteps {
    ProductSelector = 0,
    PlaceholderSelector = 1,
    VariantMatchAssigner = 2
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GenericPodAssigner',
  props: {
    templateProductId: null,
    podProvider: { default: '' }
  },
  emits: ['saved', 'cancel'],
  setup(__props: any, { emit }) {

const props = __props as { templateProductId: string, podProvider: string };





const currentStepIdx = ref(0);
const components = [ProductSelector, PlaceholderSelector, VariantMatchAssigner];

let currentFilters: unknown;
const isLoading = ref(false);
const catalog: Ref<GenericCatalog> = ref({
    products: []
} as GenericCatalog);
const fetchPage = ref(1);
const selectedProviderProduct = ref({} as GenericPodProduct);
const templateProduct = ref({} as TemplateProductVariant);
const isVariantPickerOpen = ref(false);
const selectedPlaceholder = ref('');
const selectedVariant: Ref<Variant | null> = ref(null);
const lastVariantLinkedElement: Ref<string | null> = ref(null);
const variantPrices = ref<Record<string, number>>({});

const currentStep = computed(() => {
    return components[currentStepIdx.value];
});

const isInitialStep = computed(() => {
    return currentStepIdx.value == 0;
});

const isFinalStep = computed(() => {
    return currentStepIdx.value == components.length - 1;
});

const isNextDisabled = computed(() => {
    if (isLoading.value) return true;

    return isFinalStep.value
        ? templateProduct.value.variants.some((v) => !v.podVariantId)
        : !selectedPlaceholder.value?.length && !(selectedProviderProduct.value?.placeholders?.length === 1);
});

const displayCatalogFilters = computed(() => {
    return [Provider.CUSTOMCAT, Provider.TEELAUNCH].includes(props.podProvider as never);
});

const showFilters = computed(() => {
    return (
        (isVariantPickerOpen.value && selectedProviderProduct.value.filters?.length) ||
        (isInitialStep.value && displayCatalogFilters.value)
    );
});

onBeforeMount(async () => {
    if (!getVariantDraftData()) {
        await loadData();
    }    
});

const loadData = async () => {
    const promises: Promise<unknown>[] = [
        getProductCatalog(),
        getTemplateProductVariants()
    ];
    
    if (displayCatalogFilters.value) {
        promises.push(getProductFilters());
    }
    await Promise.all(promises);
};

const getProductCatalog = async (page = 1, filters?: unknown) => {
    isLoading.value = true;
    const podCatalog = await getPodProductsCatalog(props.podProvider, page, filters);
    catalog.value.products = page > 1 ? catalog.value.products.concat(podCatalog.products) : podCatalog.products;
    catalog.value.fetchLimit = podCatalog.fetchLimit;
    isLoading.value = false;
};

const getTemplateProductVariants = async () => {
    const product = await getProductManualVariants(props.templateProductId);
    templateProduct.value = product;
};

const getPodProductVariants = async (filters?: unknown) => {
    const variants = await _getPodProductVariants(props.podProvider, selectedProviderProduct.value.id, filters);
    selectedProviderProduct.value.variants = variants;
};

const getPodProductDetails = async () => {
    isLoading.value = true;
    const product = await _getPodProductDetails(props.podProvider, selectedProviderProduct.value.id);
    selectedProviderProduct.value.placeholders = product.placeholders;
    selectedProviderProduct.value.variants = product.variants;
    isLoading.value = false;
};

const getProductFilters = async (productId?: string) => {
    if (!selectedProviderProduct.value?.filters && productId) return;
    const filters = await getPodProductFilters(props.podProvider, productId);
    if (productId) {
        selectedProviderProduct.value.filters = filters;
        return;
    }
    catalog.value.filters = filters;
};

const onManualFetchProducts = async () => {
    fetchPage.value++;
    await getProductCatalog(fetchPage.value, currentFilters);
};

const loadDependenciesForVariantMatch = async () => {
    isLoading.value = true;
    const promises = [getTemplateProductVariants()];

    if (!selectedProviderProduct.value?.variants.length) {
        promises.push(getPodProductVariants());
    }

    if (!selectedProviderProduct.value?.filters?.length) {
        promises.push(getProductFilters(selectedProviderProduct.value.id));
    }

    await Promise.all(promises);
    populatePodVariantDetails();
    isLoading.value = false;
};

const onNextHandler = async () => {
    // If we're on the final step, collect prices before moving to next
    if (isFinalStep.value) {
        variantPrices.value = getVariantPricesFromTemplate();
    }
    
    currentStepIdx.value++;
    switch (currentStepIdx.value) {
        case AssignerSteps.PlaceholderSelector:
            await loadDependenciesForVariantMatch();
            if (!selectedProviderProduct.value?.placeholders?.length) {
                await getPodProductDetails();
            }
            /**
             * Skip print area step if there is no print area
             * available after get product details
             */
            if (!selectedProviderProduct.value?.placeholders?.length) {
                await onNextHandler();
            }
            break;
        case components.length:
            await linkToPod();
            break;
    }
};

const linkToPod = async () => {
    isLoading.value = true;
    
    try {
        const params: MatchVariantAndPlaceholderPayload = {
            variantsMatched: templateProduct.value.variants.map<Variant>((variant) => {
                return {
                    ...variant,
                    printArea: variant.printArea || selectedPlaceholder.value,
                    podProductId: selectedProviderProduct.value.id
                };
            }),
            placeholdersMatched: templateProduct.value.placeholders,
            templateProductId: props.templateProductId
        };

        await linkManualVariants(params);
        
        if (Object.keys(variantPrices.value).length > 0) {
            const provider = props.podProvider as Provider;
            await saveVariantsPrice(provider, selectedProviderProduct.value.id, variantPrices.value);            
        }

        isLoading.value = false;
        emit('saved');
        localStorage.removeItem(props.templateProductId);
    } catch (error) {
        isLoading.value = false;
        currentStepIdx.value--;
        throw error;
    }
};

const onProductSelected = async (product: GenericPodProduct) => {
    selectedProviderProduct.value = product;
    onNextHandler();
};

const onFiltersChange = async (filters?: unknown) => {
    isLoading.value = true;
    switch (currentStepIdx.value) {
        case AssignerSteps.VariantMatchAssigner:
            await getPodProductVariants(filters);
            break;
        case AssignerSteps.ProductSelector:
            currentFilters = filters;
            fetchPage.value = 1;
            await getProductCatalog(fetchPage.value, filters);
            break;
    }
    isLoading.value = false;
};

// TODO: use idx update instead of reference values
const variantMatch = (variant: Variant, elementId: string) => {
    selectedVariant.value = variant;
    lastVariantLinkedElement.value = elementId;
    isVariantPickerOpen.value = true;
};

const onVariantSelected = (variant: GenericPodVariant) => {
    selectedVariant.value!.podVariantId = variant.id;
    selectedVariant.value!.podVariantName = variant.title;
    selectedVariant.value!.printArea = variant.placeholderId;    
    // Force Vue to re-render by triggering reactivity
    templateProduct.value = { ...templateProduct.value };
    closeVariantPicker();
};

const getVariantDraftData = (): boolean => {
    const matchData = localStorage.getItem(props.templateProductId);
    if (matchData) {
        const parsedMatchData = JSON.parse(matchData);
        selectedProviderProduct.value = { ...parsedMatchData.product };
        selectedPlaceholder.value = parsedMatchData.placeholder;
        templateProduct.value = { ...parsedMatchData.templateProduct };
        currentStepIdx.value = AssignerSteps.VariantMatchAssigner;
    }
    return matchData != null;
};

const populatePodVariantDetails = async () => {
    // Set pod variant name
    if (templateProduct.value?.variants && selectedProviderProduct.value?.variants) {
        templateProduct.value.variants.forEach(variant => {
            if (variant.podVariantId) {
                const podVariant = selectedProviderProduct.value.variants.find(
                    pv => pv.id === variant.podVariantId
                );
                if (podVariant) {
                    variant.podVariantName = podVariant.title;
                }
            }
        });

        // Load existing prices for variants that have podVariantId
        try {
            const variantIds = templateProduct.value.variants
                .map(variant => variant.podVariantId)
                .filter((id): id is string => id !== undefined);
            
            if (variantIds.length > 0) {
                const existingPrices = await getVariantsPrice(props.podProvider, variantIds);
                
                // Update the variants with existing prices
                templateProduct.value.variants.forEach(variant => {
                    if (variant.podVariantId) {
                        const price = existingPrices[String(variant.podVariantId)];
                        if (price !== undefined) {
                            variant.wholesalePrice = Number(price);
                        } else {
                            // Set default price if no existing price found
                            variant.wholesalePrice = variant.wholesalePrice || 0;
                        }
                    }
                });
            }
        } catch (error) {
            console.error('Error loading existing prices:', error);
            // Set default prices if loading fails
            templateProduct.value.variants.forEach(variant => {
                if (variant.podVariantId) {
                    variant.wholesalePrice = variant.wholesalePrice || 0;
                }
            });
        }
    }
};

const getCurrentStepFilters = (): ProductFilters[] => {
    let filters: ProductFilters[] | undefined;
    switch (currentStepIdx.value) {
        case AssignerSteps.ProductSelector:
            filters = catalog.value.filters;
            break;
        case AssignerSteps.VariantMatchAssigner:
            filters = selectedProviderProduct.value.filters;
            break;
    }

    return filters || [];
};

const onPlaceholderSelected = (placeholder: string, idx: number) => {
    selectedPlaceholder.value = '';
    templateProduct.value.placeholders[idx].providerPlaceholderId = placeholder;
    for (const pHolder of templateProduct.value.placeholders) {
        if (!pHolder.providerPlaceholderId.length) continue;
        if (selectedPlaceholder.value.includes(pHolder.providerPlaceholderId)) continue;
        if (!selectedPlaceholder.value.length) {
            selectedPlaceholder.value = pHolder.providerPlaceholderId;
            continue;
        }
        selectedPlaceholder.value = selectedPlaceholder.value.concat(`,${pHolder.providerPlaceholderId}`);
    }
};

const onCancelHandler = () => {
    emit('cancel');
};

const closeVariantPicker = () => {
    isVariantPickerOpen.value = false;
    selectedVariant.value = null;
    const matchData = {
        product: { ...selectedProviderProduct.value },
        placeholder: selectedPlaceholder.value,
        templateProduct: { ...templateProduct.value }
    };

    localStorage.setItem(props.templateProductId, JSON.stringify(matchData));

    // Waiting for the DOM to update
    nextTick(() => {
        document.getElementById(lastVariantLinkedElement.value!)?.scrollIntoView(true);
    });
};

const onBackHandler = () => {
    currentStepIdx.value--;
};

const getVariantPricesFromTemplate = () => {
    const prices: Record<string, number> = {};
    
    templateProduct.value.variants.forEach((variant) => {
        if (variant.podVariantId && variant.wholesalePrice !== undefined && variant.wholesalePrice >= 0) {
            // Convert to number to ensure it's not sent as a string
            prices[variant.podVariantId] = Number(variant.wholesalePrice);
        }
    });
    
    return prices;
};


return { __sfc: true,AssignerSteps, emit, props, currentStepIdx, components, currentFilters, isLoading, catalog, fetchPage, selectedProviderProduct, templateProduct, isVariantPickerOpen, selectedPlaceholder, selectedVariant, lastVariantLinkedElement, variantPrices, currentStep, isInitialStep, isFinalStep, isNextDisabled, displayCatalogFilters, showFilters, loadData, getProductCatalog, getTemplateProductVariants, getPodProductVariants, getPodProductDetails, getProductFilters, onManualFetchProducts, loadDependenciesForVariantMatch, onNextHandler, linkToPod, onProductSelected, onFiltersChange, variantMatch, onVariantSelected, getVariantDraftData, populatePodVariantDetails, getCurrentStepFilters, onPlaceholderSelected, onCancelHandler, closeVariantPicker, onBackHandler, getVariantPricesFromTemplate, VariantPicker, VairantFilters }
}

})